import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';

import { LoginCredentialsTypeEnum } from '@codefluegel/wowis-rest-typescript-client';
import { Alert, Paper, Snackbar, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { observer } from 'mobx-react-lite';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthApi } from '../ApiClient';
import InformPasswordResetModal from '../components/dialog/InformPasswordResetModal';
import { useStore } from '../store/context';
import { isPortrait } from '../utils';

const ATTEMPT_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

const getLoginAttempts = (): number => {
  const saved = localStorage.getItem('loginAttempts');
  if (!saved) return 0;

  try {
    const { count, timestamp } = JSON.parse(saved);

    if (Date.now() - timestamp > ATTEMPT_EXPIRY_MS) {
      localStorage.removeItem('loginAttempts');
      return 0;
    }

    return count;
  } catch {
    localStorage.removeItem('loginAttempts');
    return 0;
  }
};

const storeLoginAttempt = (currentAttempts: number): number => {
  const newAttempts = currentAttempts + 1;

  const attemptData = {
    count: newAttempts,
    timestamp: Date.now(),
  };
  localStorage.setItem('loginAttempts', JSON.stringify(attemptData));
  return newAttempts;
};

const clearLoginAttempts = (): void => {
  localStorage.removeItem('loginAttempts');
};

const LoginPage = observer((): JSX.Element => {
  const { authStore } = useStore();
  const authApi = useAuthApi();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isPending, setPending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const [loginAttempts, setLoginAttempts] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    setPending(true);
    event.preventDefault();
    try {
      const { data: authResponse } = await authApi.getLoginTokens({
        username: username.toLowerCase(),
        password,
        grant_type: 'password',
        type: LoginCredentialsTypeEnum.Customer,
      });
      if (authResponse.success) {
        authStore.setUserData(authResponse);
        clearLoginAttempts();
        navigate('/');
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      if (error instanceof Error && typeof error.message === 'string') {
        setSnackbarMessage('Username oder Passwort falsch!');
        setSnackbarOpen(true);

        const newAttempts = storeLoginAttempt(loginAttempts);
        setLoginAttempts(newAttempts);

        if (newAttempts >= 3) {
          setOpenInfoModal(true);
        }
      }
      setPending(false);
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    if (authStore.userData?.access_token) {
      navigate('/');
    }

    setLoginAttempts(getLoginAttempts());
  }, []);

  return (
    <Container
      component="main"
      maxWidth="xs"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '85vh',
      }}
    >
      <Box
        sx={{
          display: isPortrait() ? 'block' : 'flex',
        }}
      >
        <Paper sx={{ padding: 1 }} elevation={0}>
          <Typography
            component="h1"
            variant="h4"
            align="center"
            color="secondary"
          >
            Login
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              sx={{ input: { color: '#58585A' } }}
              variant="standard"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Benutzername"
              name="email"
              autoComplete="email"
              autoFocus
              value={username}
              onChange={e => setUsername(e.target.value)}
              error={!!snackbarMessage}
            />

            <TextField
              sx={{ input: { color: '#58585A' } }}
              variant="standard"
              required
              fullWidth
              name="password"
              label="Passwort"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              helperText={snackbarMessage}
              error={!!snackbarMessage}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isPending}
                sx={{ paddingX: 4, mt: 3, mb: 2 }}
                color="primary"
              >
                Login
              </Button>
            </Box>
          </Box>
          <Link to="/passwort-link-anfordern">
            <Typography
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: 'grey',
                textDecoration: 'underline',
              }}
              variant="subtitle1"
            >
              Passwort zurücksetzen
            </Typography>
          </Link>
        </Paper>
      </Box>
      <InformPasswordResetModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
});

export default LoginPage;
